import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { createLink } from '@tanstack/react-router';

import WinningCpmBellCurve from './WinningCpmBellCurve';

import usePlatformTraffickingLandingRoute from '@/features/navigation/hooks/usePlatformTraffickingLandingRoute';
import ActionIcon from '@/features/offlineSalesOptimizer/components/howItWorks/ActionIcon';
import { Button } from '@/shared/components/button';

const LinkButton = createLink(Button);

const HowItWorks = () => {
  const traffickingLink = usePlatformTraffickingLandingRoute();

  return (
    <Box pt={1}>
      <Box
        p={3}
        m={2}
        borderRadius={1}
        sx={{ float: 'right', bgcolor: 'background.paper' }}
      >
        <ActionIcon sx={{ fontSize: 48 }} />

        <Typography pt={2.5}>Review your optimizations</Typography>
        <Typography pt={1} pb={3}>
          Make changes to campaigns and ad groups.
        </Typography>

        <LinkButton to={traffickingLink} variant='contained' fullWidth>
          Go to Trafficking
        </LinkButton>
      </Box>

      <Box>
        <Typography color='text.tertiary' variant='h6' component='h2'>
          Context
        </Typography>
        <Typography
          py={2}
          component='p'
          variant='subtitleNumbers'
          fontSize='1rem'
        >
          Media, traditionally a negotiated pricing model, has largely
          transitioned into a series of auction-based marketplaces, purchased
          through biddable media.
          <br />
          <br />
          Once a cost is input** into a biddable media platform, the auction
          system tries to understand the following three things:
        </Typography>

        <Box
          borderRadius={1}
          p={2}
          sx={{ bgcolor: (theme) => theme.palette.primary[50] }}
          display='inline-block'
        >
          <Typography component='ul' p={1} px={3} color='primary.dark'>
            <li>
              How do I spend all of the money the advertiser has budgeted?
            </li>
            <li>How do I do this at the lowest cost to the advertiser?</li>
            <li>How do I do this at the lowest cost to the platform?</li>
          </Typography>
        </Box>

        <Typography
          py={2}
          component='p'
          variant='subtitleNumbers'
          fontSize='1rem'
        >
          **In instances where the campaign is set to auto-bid, the platforms
          are starting with an arbitrary CPM - that is to say, the starting bid
          is determined with an appropriate estimate and does not consider the
          above.
          <br />
          <br />
          This has created an environment wherein during the first few days of
          any biddable media campaign, the platform must determine how many
          auctions the advertiser must participate in to to answer the above 3
          questions and achieve the corresponding optimal result.
        </Typography>

        <Box
          borderRadius={1}
          p={2}
          sx={{ bgcolor: (theme) => theme.palette.primary[50] }}
          display='inline-block'
        >
          <Typography color='primary.dark'>
            This learning period alters an advertiser&apos;s fundamental supply
            and demand economics.
          </Typography>
        </Box>

        <Typography pt={4} color='text.tertiary' variant='h6' component='h2'>
          Process
        </Typography>

        <Typography
          py={2}
          component='p'
          variant='subtitleNumbers'
          fontSize='1rem'
        >
          The process described above is not advantageous to the advertiser due
          to supply & demand economics. The result is constrained supply, and
          when supply is altered while demand stays the same, the ability to
          continually find the lowest actual media cost is inhibited - impacting
          the total amount of actual auctions vs. how much an advertiser needs
          to spend.
          <br />
          <br />
          Therefore, by continuing to lower the bid, the platform is forced to
          reassess the above three priorities and resets the advertiser&apos;s
          fundamental supply and demand economics, while ensuring the media is
          driven at a lower costs, or else constraining media spend over time.
          <br />
          <br />
          For media buyers ambiguity will always exist. Buyers tend to think in
          terms of averages when it comes to CPM, however in reality CPMs
          fluctuate across a wide distribution as a result of a variety of
          factors.
        </Typography>

        <Typography pb={2} variant='body2'>
          Distribution of Winning CPM
        </Typography>

        <WinningCpmBellCurve />

        <Typography pt={4} color='text.tertiary' variant='h6' component='h2'>
          Results
        </Typography>
        <Typography
          py={2}
          component='p'
          variant='subtitleNumbers'
          fontSize='1rem'
        >
          The level of supply and demand is unknown at any given moment (y-axis
          in the above illustration) and fluctuates regularly based on a number
          of factors that influence consumer behavior such as time of day, day
          of week, seasonality, etc. While we can begin to identify certain more
          obvious patterns of consumer behavior (i.e. media consumption in the
          middle of the night 1am-3am is typically much lower than media
          consumption during someone&apos;s lunch hour at work 12pm-2pm), we
          will never know exactly what is occurring.
          <br />
          <br />
          The CPM necessary to win an auction at any given moment (x-axis in the
          above illustration) also fluctuates. Typically the more demand the
          higher the cost and the less demand the lower the cost. Because demand
          is constantly fluctuating, the CPM necessary to win an auction is also
          difficult to predict.
          <br />
          <br />
          The only finite constraint in this scenario is budget. By default the
          priority for DSPs is spending budget in full which therefore
          supersedes spending budget at the lowest cost. As an advertiser the
          priority is always to spend the budget most efficiently and to
          eliminate any potential waste.
          <br />
          <br />
          Platforms prioritize spend first and lower cost second. Starvation
          Bidder provides automation to prioritize platforms to a lower cost
          first and spend second which can save advertisers 15-23% on their cost
          per mille.
        </Typography>

        <Box
          borderRadius={1}
          p={2}
          sx={{ bgcolor: (theme) => theme.palette.primary[50] }}
          display='inline-block'
        >
          <Typography color='primary.dark'>
            Starvation Bidder lowers bids initially to try and starve the buy to
            under-deliver.
          </Typography>
        </Box>

        <Typography
          py={2}
          component='p'
          variant='subtitleNumbers'
          fontSize='1rem'
        >
          When this happens the platform algorithms will first attempt to
          rightsize the underdelivery through auction shaping. Auction shaping
          is the DSP changing the number of auctions an advertiser has available
          to them as a result of their bid to encourage delivering their budget
          in full. So if the platform sees that after lowering the bid an
          advertiser is no longer winning enough auctions to spend in full, they
          will open up the number of auctions available to them. If after a day
          or two the campaign is back to pacing in full then there is an
          opportunity for the Starvation Bidder to lower the bids further and
          the process repeats itself until the bid has been lowered just enough
          to spend in full without any excess cost.
        </Typography>

        <Box
          borderRadius={1}
          p={2}
          sx={{ bgcolor: (theme) => theme.palette.primary[50] }}
          display='inline-block'
        >
          <Typography color='primary.dark'>
            It is imperative to be patient and not panic in this initial phase
            of under-delivery. An important component of the Starvation Bidder
            is the phase of trial and error before the sweet spot for the bid is
            found. This may mean a day or two of under-delivery before getting
            back on track. This also means that as you near the final days of a
            campaign flight you may want to be less aggressive with the use of
            the Starvation Bidder in order to not jeopardize delivering in full.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default HowItWorks;
